import type { ReactElement } from 'react'
import { NavLink } from 'react-router-dom'

export interface SubMenuItem {
  label: string
  href: string
}

export interface MenuItem {
  label: string
  href?: string
  onClick?: () => void
  subItems?: SubMenuItem[]
}

interface DropdownMenuProps {
  item: MenuItem
  isOpen: boolean
  toggleDropdown: (label: string) => void
  isMobile?: boolean
}

export function DropdownMenu({
  item,
  isOpen,
  toggleDropdown,
  isMobile = false,
}: DropdownMenuProps): ReactElement {
  return (
    <div className={isMobile ? 'w-full' : 'relative'}>
      <button
        type="button"
        onClick={() => toggleDropdown(item.label)}
        className={`flex items-center gap-1.5 rounded-xl px-3.5 py-1.5 font-medium text-sm transition-all ${
          isMobile
            ? 'w-full justify-between py-2 text-gray-700 hover:bg-gray-100'
            : ''
        } ${
          isOpen
            ? 'bg-gray-100 font-semibold text-gray-900'
            : 'text-gray-600 hover:bg-gray-100/80 hover:text-gray-900'
        }`}
      >
        <span>{item.label}</span>
        <svg
          className={`h-4 w-4 transform text-gray-400 transition-transform duration-150 ${
            isOpen ? 'rotate-180 text-gray-700' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          role="img"
        >
          <title>Toggle {item.label} menu</title>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {isOpen && item.subItems && (
        <SubMenuItems items={item.subItems} isMobile={isMobile} />
      )}
    </div>
  )
}

interface SubMenuItemsProps {
  items: SubMenuItem[]
  isMobile?: boolean
}

function SubMenuItems({
  items,
  isMobile = false,
}: SubMenuItemsProps): ReactElement {
  const containerClass = isMobile
    ? 'mt-1 ml-3 space-y-1 border-l-2 border-indigo-100 pl-3 py-1'
    : 'absolute left-0 mt-2 w-52 overflow-hidden rounded-2xl border border-gray-200/80 bg-white/95 p-1.5 shadow-xl backdrop-blur-md ring-1 ring-black/5 z-50'

  return (
    <div className={containerClass}>
      {items.map((subItem) => {
        const isExternal = subItem.href.startsWith('http')
        if (isExternal) {
          return (
            <a
              key={subItem.label}
              href={subItem.href}
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-xl px-3 py-2 font-medium text-gray-700 text-sm transition-colors hover:bg-gray-100 hover:text-gray-900"
            >
              {subItem.label}
            </a>
          )
        }

        return (
          <NavLink
            key={subItem.label}
            to={subItem.href}
            className={({ isActive }) =>
              `block rounded-xl px-3 py-2 font-medium text-sm transition-colors ${
                isActive
                  ? 'bg-indigo-50 font-semibold text-indigo-700'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              }`
            }
          >
            {subItem.label}
          </NavLink>
        )
      })}
    </div>
  )
}
